import {
  getInstancePathname,
  getInstanceStatus,
  getInstanceUrl,
} from "@stores/instances/utils";
import {
  canTryProPlan,
  getProTrial,
  isCanceledOrDeleted,
} from "@stores/purchases/utils";
import { isDevPlan, isProPlan } from "@common/utils/plan";
import {
  ActionIcon,
  Anchor,
  Box,
  Flex,
  List,
  Menu,
  Text,
  Title,
} from "@mantine/core";
import { persistUTMQueryParamsToString } from "@common/utils/router";
import { useRouter } from "next/router";
import Link from "next/link";
import SettingsLink from "../section/SettingsLink";
import { IPurchase } from "@stores/purchases/types";
import HealthStatus from "@common/components/ui/health/HealthStatus/HealthStatus";
import IconDots from "@common/components/icons/IconDots/IconDots";
import { UpsellPill } from "@components/ui/upsells";
import { useTryProPlan } from "@components/forms/trial/ProTrialForm/ProTrialForm";
import ErrorMessage from "@common/components/text/ErrorMessage";
import { TRIAL_UP_ACTIVE } from "@stores/purchases/constants";
import { formatDate, getDaysCountBetweenDates } from "@utils/date";
import { PurchaseCardHeader } from "../PurchaseCard/PurchaseCard";
import { DevBadge } from "@components/ui/plan/DevBadge/DevBadge";

function CloudPurchaseCardTitle({
  href,
  healthStatus,
  children,
}: {
  href?: string;
  healthStatus?: React.ReactNode;
  children: React.ReactNode;
}) {
  const title = (
    <Title
      m={0}
      order={2}
      size="h3"
      display="flex"
      pos="relative"
      style={{ wordBreak: "break-word" }}
    >
      {healthStatus && (
        <Flex
          pr="0.25rem"
          h="1lh"
          pos={{ base: "absolute", sm: "static" }}
          right="100%"
        >
          {healthStatus}
        </Flex>
      )}
      {children}
    </Title>
  );

  return href ? (
    <Anchor href={href} target="_blank" rel="noreferrer" c="gray.6">
      {title}
    </Anchor>
  ) : (
    title
  );
}

export function CloudPurchaseCardHeader({ purchase }: { purchase: IPurchase }) {
  const router = useRouter();
  const { hosted_instance, id } = purchase;
  if (!hosted_instance) {
    return null;
  }
  const { alias_url, dns_alias } = hosted_instance;
  const instanceUrl = getInstanceUrl({ alias_url, dns_alias });
  const isPurchaseCanceledOrDeleted = isCanceledOrDeleted(purchase);
  const instanceHealth = hosted_instance && getInstanceStatus(hosted_instance);
  const hasHealthStatus = !isPurchaseCanceledOrDeleted && instanceHealth;
  const isDev = isDevPlan(purchase.product.plan.alias);

  const settingsUrl = (section: string) =>
    persistUTMQueryParamsToString(router, `/account/${id}/settings#${section}`);

  return (
    <PurchaseCardHeader
      title={
        <CloudPurchaseCardTitle
          href={isPurchaseCanceledOrDeleted ? undefined : instanceUrl}
          healthStatus={
            hasHealthStatus && (
              <HealthStatus health={instanceHealth} indicatorSize={10} />
            )
          }
        >
          {getInstancePathname({ alias_url, dns_alias })}
        </CloudPurchaseCardTitle>
      }
      titleExtra={
        isDev ? (
          <DevBadge ml="xs" />
        ) : !isPurchaseCanceledOrDeleted ? (
          <Menu withinPortal>
            <Menu.Target>
              <ActionIcon aria-label="Common actions" ml="xs">
                <IconDots />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                component={Link}
                href={settingsUrl("settings-dns-alias")}
              >
                Configure DNS alias
              </Menu.Item>
              <Menu.Item
                component={Link}
                href={settingsUrl("settings-hosting-region")}
              >
                Change hosting region
              </Menu.Item>
              <Menu.Item
                component={Link}
                href={settingsUrl("settings-custom-domain")}
              >
                Manage custom domain
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : null
      }
      headerExtra={
        !isPurchaseCanceledOrDeleted && (
          <SettingsLink purchaseId={purchase.id} />
        )
      }
    />
  );
}

function UpgradeToProCTA({ purchase }: { purchase: IPurchase }) {
  const { isLoading, error, hasStartedTrial, onSubmit } = useTryProPlan({
    purchase,
  });
  if (isProPlan(purchase.product.plan.alias)) {
    return null;
  }
  const currentProTrialUp = getProTrial(purchase);
  const isProTrialAllowed = !hasStartedTrial && canTryProPlan(purchase);
  const currentProTrialUpDaysOffset = currentProTrialUp
    ? getDaysCountBetweenDates(currentProTrialUp.expires_at, Date.now())
    : null;

  return (
    <div>
      <Flex align="center" mt="md">
        <Text size="sm">
          {currentProTrialUp?.status === TRIAL_UP_ACTIVE || hasStartedTrial
            ? "Free 14-day Pro trial is in progress"
            : "Want access to auditing, dependencies, SSO and more?"}
        </Text>
        <Box ml="auto">
          {isProTrialAllowed ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              data-testid="pro-trial-form"
            >
              <UpsellPill
                campaign="overview-card"
                isSubmit
                disabled={isLoading}
              />
            </form>
          ) : (
            <UpsellPill campaign="overview-card" instanceId={purchase.id} />
          )}
        </Box>
      </Flex>
      {currentProTrialUp?.status === TRIAL_UP_ACTIVE && (
        <Text fz="sm" m={0}>
          Trial will end{" "}
          {currentProTrialUpDaysOffset === 0 ? (
            <Text span c="red.3" fw={700}>
              today
            </Text>
          ) : currentProTrialUpDaysOffset === 1 ? (
            <Text span c="red.3" fw={700}>
              tomorrow
            </Text>
          ) : (
            <>
              on <b>{formatDate(currentProTrialUp.expires_at, "long")}</b>
            </>
          )}
        </Text>
      )}
      <ErrorMessage error={error} />
    </div>
  );
}

export function CloudPurchaseCardFooter({ purchase }: { purchase: IPurchase }) {
  return (
    <Box>
      <UpgradeToProCTA purchase={purchase} />
      <Text mb="xs" mt="xl">
        Common next steps
      </Text>
      <List>
        <List.Item>
          <Anchor component={Link} href={`/account/${purchase.id}/migrate`}>
            Migrate from a self-hosted install
          </Anchor>
        </List.Item>
        <List.Item>
          <Anchor
            href="https://metabase.com/cloud/docs/custom-domain"
            target="_blank"
            rel="noreferrer"
          >
            Set up a custom domain
          </Anchor>
        </List.Item>
      </List>
    </Box>
  );
}
